/**
 * i18n setup for the end-user app.
 *
 * - Spanish is the default language and the fallback for any missing key.
 * - The selected language is persisted in localStorage so it survives
 *   refreshes, tab closures, browser restarts and logout/login cycles.
 * - If the stored value is missing or invalid, we fall back to Spanish.
 */

import { createI18n } from 'vue-i18n'
import es from './locales/es'
import en from './locales/en'

export const SUPPORTED_LOCALES = ['es', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'es'

const STORAGE_KEY = 'pg_pharma_locale'

export function isSupportedLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * Reads the persisted locale, falling back to Spanish when the value is
 * missing, unreadable (storage disabled) or corrupted.
 */
export function readStoredLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLocale(stored)) {
      return stored
    }
  } catch {
    // localStorage may be unavailable (private mode / disabled) — ignore.
  }
  return DEFAULT_LOCALE
}

export function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Persisting is best-effort; ignore storage failures.
  }
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: readStoredLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { es, en },
})

/** The currently active locale (typed). */
export function currentLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale
}

/**
 * Translate outside of a component (services, stores, plain modules) using the
 * global i18n instance. Components should keep using `useI18n()` for reactivity.
 */
export function t(key: string, named?: Record<string, unknown>): string {
  return named ? i18n.global.t(key, named) : i18n.global.t(key)
}

/**
 * Switches the active language, persists it and reflects it on the
 * document element so assistive tech and the browser pick it up.
 */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  persistLocale(locale)
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', locale)
  }
}

/** Applies the persisted locale to the document on startup. */
export function initLocale(): void {
  setLocale(readStoredLocale())
}

export default i18n
